import React from 'react';
import { createRoot } from 'react-dom/client';
import merge from 'lodash.merge';
import configureStore, { history } from './configureStore';
import Main from './main';
import {
  parseInitialData,
  transformInitialDataToPageState,
  transformInitialDataToConfigState,
  removeInitScript,
  clearInitialData,
  hasInitialData
} from './utils/initialDataParser';
import { parseNewFormat, mergeLocalVariablesIntoGlobal, attachStyle, replacePlaceholdersWithVariables, applyDocumentLanguage, migrateSchemeNode } from './utils/base';
import { expandTemplateRefs, withTemplateVariables } from './utils/screenTemplates';
import { applyStyleClasses } from './utils/styleClasses';
import SessionHelper from './utils/sessionHelper';
import DeviceHelper from './utils/deviceHelper';
import retryWithHeaders, { RetryWithHeadersResult } from './utils/retryWithHeaders';
import { updateEndpointParams } from './store/config/actions';
import { EndpointParams } from './store/config/types';
import { NavContext, replaceNavState } from './utils/navigationContext';
import { normalizeAppData } from './utils/normalizeAppData';
import { hydrateDesignSystem, setThemeChangeListener } from './utils/designSystem';
import { isFrameMode } from './utils/req';
import { clear as clearScreenCache } from './utils/indexedDbHelper';

/**
 * Extracts the pathname from a URL
 * @param url - Full URL (e.g., https://demo.gom.com/okauy/)
 * @returns Pathname if it's more than just '/', otherwise empty string
 * @example extractPathFromUrl('https://demo.gom.com/okauy/') => '/okauy/'
 * @example extractPathFromUrl('https://demo.gom.com/') => ''
 */
const extractPathFromUrl = (url: string): string | undefined => {
  try {
    const parsedUrl = new URL(url);
    const {pathname} = parsedUrl;
    // Only return pathname if it's more than just '/'
    return pathname && pathname !== '/' ? pathname : '';
  } catch (e) {
    console.error('Failed to parse URL:', e);
    return '';
  }
};

const hasScreenData = (appData: any): boolean => appData?.data?.mode === 'show' && !!appData?.data?.screen;


const buildConfigState = (appData: any) => {
  const configData = transformInitialDataToConfigState(appData);
  if (!configData) return undefined;

  const publicUrlPath = appData.data.config?.public_url
    ? extractPathFromUrl(appData.data.config.public_url)
    : undefined;

  if (appData.data.config?.style) {
    attachStyle(appData.data.config.style);
  }

  return {
    ...configData,
    initializedFromInitScript: true,
    publicUrl: publicUrlPath || undefined,
  };
};

const buildPageState = (appData: any) => {
  const transformedData = transformInitialDataToPageState(appData);
  if (!transformedData) return undefined;

  const { page, settings, variableData } = appData.data.screen ? {
    // FE-4451 — this branch takes the screen STRAIGHT off the bootstrap payload
    // and never reaches parseNewFormat, so it has to assemble template
    // references itself. Missing it renders every placed template as an empty
    // container on first paint, and only a later navigation (which does go
    // through parseNewFormat) would appear to fix it.
    //
    // …and for the same reason it has to run the protocol migration itself.
    // `migrateSchemeNode` is what turns the legacy camelCase `activeState` into
    // the `active_state` every component actually reads (also `svgString`,
    // `scriptUrl`, `settings.afterSymbols`). Without it a published page keeps
    // the legacy key, `active_state` is undefined on every node, and NO node
    // state is ever applied — while `value` bindings, which need no migration,
    // resolve normally. That asymmetry is the whole bug: a constructed
    // component (Calendar / Select / Table / Choice) drives its entire
    // appearance through `activeState` bindings, so on a published screen its
    // placeholder stays hidden and its dropdown never opens, even though its
    // `on_launch` ran and wrote the instance correctly.
    //
    // Ordering matches parseNewFormat: assemble templates and classes FIRST, so
    // a template body — which arrives straight from the map and has never passed
    // through a screen parse — gets migrated too.
    page: migrateSchemeNode(applyStyleClasses(
      expandTemplateRefs(appData.data.screen.sections, appData.data.templates),
      appData.data.classes,
    )),
    // FE-4733 — and, for the same reason, lay the template layer under the
    // screen's variables itself; parseNewFormat does it on every other path.
    settings: withTemplateVariables(
      transformedData.settings,
      appData.data.screen.sections,
      appData.data.templates,
    ),
    variableData: { ...appData.data?.variable_data, ...appData.data?.parameters },
  } : parseNewFormat(appData.data);

  return {
    screenId: appData.screen,
    loading: false,
    data: { page },
    settings: settings || {},
    variableData: mergeLocalVariablesIntoGlobal(variableData, settings?.variables),
    formValues: {},
    styleChunks: { refs: {}, indexes: {} },
    session_data: {
      type: 'initial',
      localShimmer: false,
      deviceId: DeviceHelper.getOrCreateDeviceId(),
      sessionId: SessionHelper.getOrCreateSessionId(),
    },
  };
};

(async () => {
  // FE-4577 — settle the design-system store before anything normalizes a
  // payload or reaches the network. Never rejects; resolves 'ready' or
  // 'degraded'. Correctness does not depend on this ordering (an unhydrated
  // mirror sends `h=none`, so the response carries everything anyway) — it
  // simply avoids one wasted full delivery per session.
  //
  // Frame mode is excluded: there the schema arrives by postMessage from the
  // editor as an unsaved working copy, which must never be persisted as the
  // branch's authoritative design system.
  await hydrateDesignSystem({ enabled: !isFrameMode() });

  // Cached screens store their sections ALREADY token-substituted, so they hold
  // whichever theme was live when they were written. Registered here rather than
  // called from the store so that module keeps no dependency on the screen cache.
  setThemeChangeListener(() => { clearScreenCache(); });

  let initialState;
  let retryHeaders: RetryWithHeadersResult['headers'] | undefined;

  if (hasInitialData()) {
    try {
      // Normalized once here too: `hasScreenData` / `buildConfigState` read the
      // raw blob directly when `parseInitialData` bails, so both paths must see
      // the canonical envelope.
      const rawAppData = normalizeAppData((window as any).__APP_DATA__);
      let appData = parseInitialData();

      // If APP_DATA doesn't have mode=show + screen, retry with headers
      if (!hasScreenData(appData || rawAppData)) {
        // Extract config from initial APP_DATA so it's not lost
        if (rawAppData?.data?.config) {
          const configState = buildConfigState(rawAppData);
          if (configState) {
            initialState = { config: configState };
          }
        }
        try {
          const retryResult = await retryWithHeaders();
          if (retryResult) {
            appData = retryResult.data;
            retryHeaders = retryResult.headers;
          }
        } catch (retryError) {
          console.error('Retry with headers failed:', retryError);
        }
      }

      if (appData) {
        // Refine <html lang> from the (possibly retried) response's
        // `localization` before buildPageState — a process/actions response
        // (screen: null) drops through transformInitialDataToPageState's
        // `if (!screen) return null`, so the language would otherwise be lost
        // on cold boot even though `data.localization` is present.
        applyDocumentLanguage(appData?.data?.localization);

        const pageState = buildPageState(appData);
        if (pageState) {
          initialState = { ...initialState, page: pageState };

          // Handle settings.command URL update
          if (pageState.settings?.command) {
            const allVariables = merge({}, (pageState.settings?.variables || {}), (pageState.variableData || {}));
            const resolvedCommand = replacePlaceholdersWithVariables(pageState.settings.command, allVariables) as string;
            const newCommand = resolvedCommand.replace(/^\//, '').replace(/\/$/, '');
            const currentPath = window.location.pathname.replace(/^\//, '').replace(/\/$/, '') + window.location.search;

            if (!/\{\{[^}]+\}\}/.test(newCommand) && newCommand !== currentPath) {
              if (window.location.pathname + window.location.search !== `/${newCommand}`) {
                window.history.pushState(null, '', `/${newCommand}`);
              }
            }
          }

          // Stamp the current history entry with the request context so a
          // later back/forward onto it replays the same GET instead of
          // firing a contextless /get call. Mirrors what retryWithHeaders
          // does on cold boot.
          // NOTE: Fds is deliberately NOT stamped here. This snapshot is frozen
          // into `history.state` and replayed on back/forward, by which time a
          // design-system claim is stale. `executeNavigationRequest` — the only
          // consumer of these headers — derives it at fire time, next to Fsc.
          const initialHeaders: Record<string, string> = {
            Accept: 'application/json',
            Fc: 'web',
            Fie: 'true',
            Fsi: SessionHelper.getOrCreateSessionId(),
            Fdi: DeviceHelper.getOrCreateDeviceId(),
          };
          const ep = (retryHeaders || appData?.data?.config || {}) as Partial<EndpointParams>;
          if (ep.endpoint_id) initialHeaders.Fseid = String(ep.endpoint_id);
          if (ep.endpoint_type) initialHeaders.Fset = String(ep.endpoint_type);
          if (ep.task_ref) initialHeaders.Fref = String(ep.task_ref);
          if (ep.process_id) initialHeaders.Fspid = String(ep.process_id);

          const initialSnapshot: NavContext = {
            kind: 'action',
            url: window.location.pathname + window.location.search,
            method: 'GET',
            headers: initialHeaders,
          };
          replaceNavState(initialSnapshot);
        }
        const configState = buildConfigState(appData);
        if (configState) {
          initialState = { ...initialState, config: configState };
        }
      }

      removeInitScript();
      clearInitialData();
    } catch (error) {
      console.error('Failed to initialize with initial data:', error);
    }
  }

  const store = configureStore(initialState);

  // Dispatch response headers from retryWithHeaders into the same Redux storage
  // as handleActionRequest does via updateEndpointParams
  if (retryHeaders) {
    const { endpoint_id, endpoint_type, task_ref, process_id } = retryHeaders;
    if (endpoint_id || endpoint_type || task_ref || process_id) {
      const params: EndpointParams = {};
      if (endpoint_id) params.endpoint_id = endpoint_id;
      if (endpoint_type) params.endpoint_type = endpoint_type;
      if (task_ref) params.task_ref = task_ref;
      if (process_id) params.process_id = process_id;
      store.dispatch(updateEndpointParams(params));
    }
  }

  const root = createRoot(document.getElementById('root')!);
  root.render(<Main store={store} history={history} />);
})();
